/**
 * Распознавание ответа «результаты проценки больше не доступны».
 *
 * Результаты поиска (одиночного, группового, проценки закупки) живут на бэке
 * ограниченное время (для ГП — порядка двух недель), после чего удаляются вместе
 * с самим request_id. Любой последующий запрос данных по этому id получает от
 * upstream-сервиса `error_code: 407`, который прокси ЛК заворачивает в HTTP 500:
 *
 *   { "status": false, "signature": "lk_eplex_api",
 *     "error": { "code": 500, "message": "Неопознанная ошибка Data:
 *                {\"status\":\"err\",\"error_code\":407,
 *                 \"err\":\"Указанный id не найден при поиске по client_id и request_id\"}" } }
 *
 * Для пользователя это не сбой сервера, а нормальное «данные устарели»: красный
 * тост «Указанный id не найден при поиске по client_id и request_id» его только
 * пугает и ничего не подсказывает (а при параллельных запросах приезжает пачкой).
 * Поэтому такие ответы:
 *   - не показываем глобальным тостом (см. `apiJwt.ts`);
 *   - экран, которому нужны эти данные, объясняет ситуацию сам, спокойным
 *     инлайн-состоянием (см. «Подбор»).
 */

import { extractReadableError } from "../extractReadableError";

/**
 * Тексты upstream, означающие «данных по этому request_id больше нет».
 * Совпадение ищем по подстроке — бэк может добавлять к тексту префиксы
 * («Ошибка. Результаты поиска не найдены»).
 */
const EXPIRED_RESULT_PATTERNS: readonly RegExp[] = [
  /указанный\s+id\s+не\s+найден/i,
  /результаты\s+поиска\s+не\s+найдены/i,
];

/** Читаемый текст ошибки говорит об истёкших результатах проценки. */
export function isExpiredSearchResultsMessage(message: string | null | undefined): boolean {
  const readable = extractReadableError(message);
  if (!readable) return false;
  return EXPIRED_RESULT_PATTERNS.some((pattern) => pattern.test(readable));
}

interface ErrorEnvelope {
  error?: { message?: string };
}

/** Конверт ответа ЛК (`data`) относится к истёкшим результатам проценки. */
export function isExpiredSearchResultsEnvelope(data: ErrorEnvelope | undefined | null): boolean {
  return isExpiredSearchResultsMessage(data?.error?.message);
}

/**
 * Ошибка axios (или любая другая) относится к истёкшим результатам проценки.
 * Используется в catch-блоках, чтобы отличить «данные устарели» от реального сбоя.
 */
export function isExpiredSearchResultsError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const axiosLike = error as { response?: { data?: ErrorEnvelope }; message?: string };
  if (isExpiredSearchResultsEnvelope(axiosLike.response?.data)) return true;
  return isExpiredSearchResultsMessage(axiosLike.message);
}
